import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Cookie, Shield } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageProvider';

const CookieConsentBanner = () => {
  const { language } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [consent, setConsent] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  });

  const translations = {
    it: {
      title: "Utilizziamo i Cookie",
      titleShort: "Cookie",
      description: "Questo sito utilizza cookie per migliorare la tua esperienza. Puoi scegliere quali cookie accettare.",
      descriptionShort: "Questo sito usa cookie.",
      acceptAll: "Accetta Tutti",
      acceptSelected: "Accetta Selezionati",
      rejectAll: "Rifiuta Non Essenziali",
      customize: "Personalizza",
      necessary: "Cookie Necessari",
      necessaryDesc: "Essenziali per il funzionamento del sito. Non possono essere disabilitati.",
      functional: "Cookie Funzionali",
      functionalDesc: "Permettono funzionalità aggiuntive come preferenze lingua.",
      analytics: "Cookie Analitici",
      analyticsDesc: "Ci aiutano a capire come utilizzi il sito per migliorarlo.",
      marketing: "Cookie Marketing",
      marketingDesc: "Utilizzati per mostrarti contenuti personalizzati.",
      privacyLink: "Leggi la nostra Privacy Policy"
    },
    en: {
      title: "We Use Cookies",
      titleShort: "Cookies",
      description: "This site uses cookies to improve your experience. You can choose which cookies to accept.",
      descriptionShort: "This site uses cookies.",
      acceptAll: "Accept All",
      acceptSelected: "Accept Selected",
      rejectAll: "Reject Non-Essential",
      customize: "Customize",
      necessary: "Necessary Cookies",
      necessaryDesc: "Essential for the website to function. Cannot be disabled.",
      functional: "Functional Cookies",
      functionalDesc: "Enable additional features like language preferences.",
      analytics: "Analytics Cookies",
      analyticsDesc: "Help us understand how you use the site to improve it.",
      marketing: "Marketing Cookies",
      marketingDesc: "Used to show you personalized content.",
      privacyLink: "Read our Privacy Policy"
    }
  };

  const t = translations[language] || translations.en;

  useEffect(() => {
    // Check if consent has been given
    const savedConsent = localStorage.getItem('cookie_consent');
    if (!savedConsent) {
      setShowBanner(true);
    } else {
      try {
        setConsent(JSON.parse(savedConsent));
      } catch (e) {
        setShowBanner(true);
      }
    }
    
    // Check viewport for compact mode
    const checkMobile = () => {
      setIsCompact(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const saveConsent = (consentData) => {
    localStorage.setItem('cookie_consent', JSON.stringify(consentData));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    
    // Enable/disable analytics based on consent
    if (consentData.analytics) {
      // Would initialize analytics here
      console.log('Analytics enabled');
    }
    
    // Hide banner immediately
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    const allConsent = { necessary: true, functional: true, analytics: true, marketing: true };
    setConsent(allConsent);
    saveConsent(allConsent);
  };

  const handleAcceptSelected = () => {
    saveConsent(consent);
  };

  const handleRejectAll = () => {
    const minConsent = { necessary: true, functional: true, analytics: false, marketing: false };
    setConsent(minConsent);
    saveConsent(minConsent);
  };

  if (!showBanner) return null;

  // Compact mobile version
  if (isCompact && !showDetails) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-2 safe-area-inset-bottom">
        <div className="max-w-lg mx-auto">
          <div className="bg-card/95 backdrop-blur-md rounded-lg shadow-lg border border-border/50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Cookie className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-sm truncate">{t.descriptionShort}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button 
                  size="sm"
                  onClick={handleAcceptAll}
                  className="h-8 px-3 text-xs"
                  data-testid="cookie-accept-all"
                >
                  {t.acceptAll}
                </Button>
                <Button 
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDetails(true)}
                  className="h-8 px-2 text-xs"
                >
                  ...
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 safe-area-inset-bottom">
      <div className="max-w-4xl mx-auto">
        <div className="glass-panel rounded-xl shadow-2xl border border-border/50 overflow-hidden">
          {/* Main Banner */}
          <div className="p-4 sm:p-6">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{t.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm mb-3 sm:mb-4">
                  {t.description}
                </p>

                {/* Cookie Details (expandable) */}
                {showDetails && (
                  <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4 p-3 sm:p-4 rounded-lg bg-secondary/30 max-h-48 overflow-y-auto">
                    {/* Necessary */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">{t.necessary}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">{t.necessaryDesc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={consent.necessary}
                        disabled
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-border flex-shrink-0"
                      />
                    </div>

                    {/* Functional */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">{t.functional}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">{t.functionalDesc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={consent.functional}
                        onChange={(e) => setConsent({ ...consent, functional: e.target.checked })}
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-border accent-primary flex-shrink-0"
                      />
                    </div>

                    {/* Analytics */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">{t.analytics}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">{t.analyticsDesc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={consent.analytics}
                        onChange={(e) => setConsent({ ...consent, analytics: e.target.checked })}
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-border accent-primary flex-shrink-0"
                      />
                    </div>

                    {/* Marketing */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-xs sm:text-sm truncate">{t.marketing}</p>
                        <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">{t.marketingDesc}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={consent.marketing}
                        onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })}
                        className="w-4 h-4 sm:w-5 sm:h-5 rounded border-border accent-primary flex-shrink-0"
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <Button 
                    onClick={handleAcceptAll}
                    className="glow-primary-hover text-xs sm:text-sm h-8 sm:h-10"
                    data-testid="cookie-accept-all"
                  >
                    {t.acceptAll}
                  </Button>
                  
                  {showDetails ? (
                    <Button 
                      variant="outline"
                      onClick={handleAcceptSelected}
                      className="text-xs sm:text-sm h-8 sm:h-10"
                      data-testid="cookie-accept-selected"
                    >
                      {t.acceptSelected}
                    </Button>
                  ) : (
                    <Button 
                      variant="outline"
                      onClick={handleRejectAll}
                      className="text-xs sm:text-sm h-8 sm:h-10"
                      data-testid="cookie-reject"
                    >
                      {t.rejectAll}
                    </Button>
                  )}
                  
                  <Button 
                    variant="ghost"
                    onClick={() => setShowDetails(!showDetails)}
                    className="text-xs sm:text-sm h-8 sm:h-10"
                    data-testid="cookie-customize"
                  >
                    {t.customize}
                  </Button>
                  
                  <a 
                    href="/privacy" 
                    className="text-xs sm:text-sm text-primary hover:underline sm:ml-auto hidden sm:inline"
                  >
                    {t.privacyLink}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
